<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import UazapiGroupsAPI from 'dashboard/api/uazapiGroups';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  inbox: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const groups = ref([]);
const isLoading = ref(true);
const isUpdating = ref(false);

const enabledIds = computed(() =>
  groups.value.filter(group => group.enabled).map(group => group.id)
);

const loadGroups = async () => {
  isLoading.value = true;
  try {
    const { data } = await UazapiGroupsAPI.get(props.inbox.id);
    groups.value = data.payload;
  } catch (error) {
    useAlert(t('INBOX_MGMT.WHATSAPP_GROUPS.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

// Manda a lista inteira, não um delta: o backend guarda a allowlist como
// substituição total (DEC-069), então uma resposta perdida nunca deixa a
// allowlist pela metade.
const toggleGroup = async id => {
  const nextEnabled = enabledIds.value.includes(id)
    ? enabledIds.value.filter(groupId => groupId !== id)
    : [...enabledIds.value, id];

  isUpdating.value = true;
  try {
    const { data } = await UazapiGroupsAPI.update(props.inbox.id, nextEnabled);
    groups.value = data.payload;
    useAlert(t('INBOX_MGMT.WHATSAPP_GROUPS.UPDATE_SUCCESS'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.WHATSAPP_GROUPS.UPDATE_ERROR'));
  } finally {
    isUpdating.value = false;
  }
};

onMounted(loadGroups);

// Exposto para o teste chamar wrapper.vm.toggleGroup diretamente — sem isso
// <script setup> mantém tudo privado ao componente.
defineExpose({ toggleGroup });
</script>

<template>
  <div class="flex flex-col gap-4 py-4">
    <div class="flex flex-col gap-1">
      <h3 class="text-heading-2 text-n-slate-12">
        {{ $t('INBOX_MGMT.WHATSAPP_GROUPS.TITLE') }}
      </h3>
      <p class="text-body-main text-n-slate-11">
        {{ $t('INBOX_MGMT.WHATSAPP_GROUPS.DESCRIPTION') }}
      </p>
    </div>

    <Spinner v-if="isLoading" />

    <p v-else-if="!groups.length" class="text-body-main text-n-slate-11">
      {{ $t('INBOX_MGMT.WHATSAPP_GROUPS.EMPTY') }}
    </p>

    <template v-else>
      <SettingsToggleSection
        v-for="group in groups"
        :key="group.id"
        :header="group.name"
        :description="
          $t('INBOX_MGMT.WHATSAPP_GROUPS.PARTICIPANTS', {
            count: group.participants_count,
          })
        "
        :model-value="group.enabled"
        @update:model-value="!isUpdating && toggleGroup(group.id)"
      />
    </template>
  </div>
</template>
